/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { requiredRule } from 'app/core/utils/field-rules';
import { useGetRepositionCandidates } from 'app/hooks/requests/transactions/use-get-reposition-candidates';
import BigNumber from 'bignumber.js';
import moment from 'moment';
import { formatCurrency } from 'app/core/utils/formatter';
import { Transaction } from 'app/types/transaction';
import { TFunction } from 'react-i18next';

/**
 * Export `valueReposition`.
 *
 * Links the transaction being edited to an existing transaction that reverses
 * its value (a payment later refunded, or an income later returned). Both
 * transactions stay in the account balance but are excluded from charts and are
 * categorized with a hidden system category — so no category/sub category fields
 * are shown here.
 */

export const valueReposition = (translate: TFunction, data: any): FormList => [
  {
    type: 'selectField',
    name: 'targetTransactionId',
    label: translate('common.labels.repositionTransaction'),
    rules: requiredRule(translate),
    options: {
      hook: useGetRepositionCandidates as any,
      hookProps: () => [data?.id],
      normalize: (items: Transaction[]) =>
        items?.map((item: Transaction) => ({
          value: item.id,
          label: `${moment(item.date ?? item.bank_date).format('DD-MM-YYYY')}: ${
            item.description ?? item.bank_description
          }  -  ${formatCurrency(item.amount)}`
        }))
    }
  },
  {
    type: 'datePickerField',
    name: 'bank_date',
    visible: values => !!values.bank_date,
    disabled: true,
    label: translate('common.labels.bank_date'),
    rules: undefined
  },
  {
    type: 'datePickerField',
    name: 'date',
    label: translate('common.labels.date'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'bank_description',
    visible: values => !!values.bank_date,
    disabled: true,
    label: translate('common.labels.bankDescription'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'description',
    label: translate('common.labels.description'),
    rules: undefined
  },
  {
    type: 'inputNumberField',
    name: 'amount',
    disabled: !!data?.account?.hasConnected,
    label: translate('common.labels.amount'),
    onChange: (value: any, form: any) => {
      const { previousBalance } = form.getValues();

      form.setValue('balance', new BigNumber(previousBalance).plus(value ?? '0').toString());
    },
    rules: undefined
  },
  ...((!data?.previousBalance
    ? []
    : [
        {
          type: 'inputNumberField',
          name: 'previousBalance',
          disabled: true,
          label: translate('common.labels.previousBalance'),
          rules: undefined
        },
        {
          type: 'inputNumberField',
          name: 'balance',
          disabled: true,
          label: translate('common.labels.balance'),
          rules: undefined
        }
      ]) as any)
];
